"use client";

/**
 * The strip above every header on the day of an event, for a client going to
 * it whose day walks a trail with a track: an invitation to say where the phone
 * is (pale, with Ativar -- pressing it is what makes the browser ask), then
 * the trail and how far along it they are (dark, with a line of progress along
 * its foot) or that they are away from it (pale again). Pressed, it opens the
 * sheet (LiveTrailSheet). Nothing at all otherwise.
 *
 * Mounted once in the shell. While it shows it marks <html data-live-strip>,
 * which is what moves every sticky header down by its height (globals.css).
 */
import { useEffect } from "react";
import { ChevronDown, MapPin, MapPinOff, X } from "lucide-react";
import { useSession } from "@/lib/session";
import { km, metres } from "@/lib/format";
import { HikingIcon } from "@/components/ui/HikingIcon";
import { dismissLiveTrail, distanceText, enableLiveTrail, liveView, openLiveTrail, setLiveClient, useLiveTrail, type LiveView } from "@/components/live/liveTrail";
import { trailTitle } from "@/components/event/labels";

/** "4,2 de 11 km · faltam 320 m de subida", or less where less is known */
export const progressText = (view: Extract<LiveView, { kind: "on" | "away" }>) => {
  if (view.live.oneWay) {
    return "Estás no trilho";
  }
  const done = km(view.place.located.along / 1000).replace(" km", "");
  const total = km(view.live.measured.length / 1000);
  const climb = view.place.climbLeft;
  return `${done} de ${total}${climb && climb >= 10 ? ` · faltam ${metres(climb)} de subida` : ""}`;
};

export function LiveTrailStrip() {
  const { ready, client } = useSession();
  const state = useLiveTrail();
  // a sheet opened by hand outside a trail's hours brings no strip with it
  const view = state.byHand ? null : liveView(state);
  const clientId = ready ? (client ? client.id : null) : undefined;

  useEffect(() => {
    if (clientId !== undefined) {
      setLiveClient(clientId);
    }
  }, [clientId]);

  const shown = !!view;
  useEffect(() => {
    document.documentElement.toggleAttribute("data-live-strip", shown);
    return () => document.documentElement.removeAttribute("data-live-strip");
  }, [shown]);

  if (!view) {
    return null;
  }

  const title = trailTitle(view.live.trail) || "Trilho de hoje";
  const frame = "live-strip sticky top-0 z-[45]";
  const row = "mx-auto flex w-full max-w-[1200px] items-center gap-2.5 px-3 desk:px-6";
  const height = { height: "var(--live-strip-h)" };

  if (view.kind === "ask") {
    return (
      <div className={`${frame} border-b border-[#cfe9d8] bg-brand-soft`} style={{ paddingTop: "var(--safe-top)" }}>
        <div className={row} style={height}>
          <MapPin size={18} className="shrink-0 text-brand" />
          <div className="min-w-0 flex-1 leading-tight">
            <b className="block truncate text-[13.5px] font-bold text-brand-dark">Hoje: {title}</b>
            <span className="block truncate text-[12px] text-[#3d6b4b]">Vê onde estás no trilho</span>
          </div>
          <button type="button" onClick={enableLiveTrail} className="pressable shrink-0 rounded-full bg-brand px-3.5 py-1.5 text-[12.5px] font-bold text-white hover:bg-brand-dark">
            Ativar
          </button>
          <button type="button" onClick={dismissLiveTrail} className="pressable -mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#3d6b4b]" aria-label="Fechar">
            <X size={17} />
          </button>
        </div>
      </div>
    );
  }

  const on = view.kind === "on";
  const second = on ? progressText(view) : view.kind === "away" ? `Estás fora do trilho · a ${distanceText(view.place.located.distance)}` : "A procurar a tua posição…";
  const progress = on && !view.live.oneWay && view.live.measured.length ? Math.min(1, view.place.located.along / view.live.measured.length) : null;

  return (
    <button
      type="button"
      onClick={() => openLiveTrail()}
      className={`${frame} block w-full text-left ${on ? "bg-brand-dark text-white" : "border-b border-[#cfe9d8] bg-brand-soft"}`}
      style={{ paddingTop: "var(--safe-top)" }}
    >
      <span className={row} style={height}>
        {on ? <HikingIcon size={18} className="shrink-0 text-accent" /> : view.kind === "away" ? <MapPinOff size={18} className="shrink-0 text-[#3d6b4b]" /> : <MapPin size={18} className="shrink-0 text-brand" />}
        <span className="min-w-0 flex-1 leading-tight">
          <b className={`block truncate text-[13.5px] font-bold ${on ? "" : "text-brand-dark"}`}>{title}</b>
          <span className={`block truncate text-[12px] ${on ? "text-[#c9e6d2]" : "text-[#3d6b4b]"}`}>{second}</span>
        </span>
        <ChevronDown size={18} className={`shrink-0 ${on ? "text-[#c9e6d2]" : "text-[#3d6b4b]"}`} />
      </span>
      {progress !== null && (
        <span className="absolute inset-x-0 bottom-0 block h-[3px] bg-white/20">
          <span className="block h-full bg-accent transition-[width] duration-700" style={{ width: `${Math.round(progress * 1000) / 10}%` }} />
        </span>
      )}
    </button>
  );
}
